import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, Check, X, Tags } from "lucide-react";
import api, { apiError } from "./api";

const inputCls = "w-full bg-white border border-military/20 rounded-lg px-3 py-2.5 text-sm text-military-dark focus:outline-none focus:border-military";

/**
 * Gerenciador genérico de taxonomias (categorias e marcas) — mesma UI, endpoints e
 * proteções; muda apenas o recurso (kind) e os textos/testids.
 */
export const TaxonomyManager = ({ kind, title, subtitle, placeholder, helpText, testidPrefix, noun }) => {
  const [items, setItems] = useState([]);
  const [newName, setNewName] = useState("");
  const [editingSlug, setEditingSlug] = useState(null);
  const [editName, setEditName] = useState("");

  const load = () => api.get(`/admin/${kind}`).then((r) => setItems(r.data)).catch((err) => toast.error(apiError(err)));
  useEffect(() => { load(); }, [kind]);

  const add = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    try {
      await api.post(`/admin/${kind}`, { name: newName.trim() });
      setNewName("");
      toast.success(`${noun} criada`);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const saveEdit = async (it) => {
    try {
      await api.put(`/admin/${kind}/${it.slug}`, { name: editName.trim(), active: it.active });
      setEditingSlug(null);
      toast.success(`${noun} atualizada — os produtos vinculados foram renomeados junto`);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const toggleActive = async (it) => {
    try {
      await api.put(`/admin/${kind}/${it.slug}`, { name: it.name, active: !it.active });
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const move = async (it, dir) => {
    await api.put(`/admin/${kind}/${it.slug}/move`, { direction: dir }).catch((err) => toast.error(apiError(err)));
    load();
  };

  const remove = async (it) => {
    if (!window.confirm(`Excluir "${it.name}"? Essa ação não pode ser desfeita.`)) return;
    try {
      await api.delete(`/admin/${kind}/${it.slug}`);
      toast.success(`${noun} excluída`);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div data-testid={`admin-${kind}`}>
      <h1 className="font-serif text-3xl md:text-4xl text-military-dark mb-2">{title}</h1>
      <p className="text-sm text-military-dark/50 mb-8 max-w-2xl">{subtitle}</p>

      <div className="max-w-2xl space-y-6">
        <form onSubmit={add} className="bg-white border border-military/10 rounded-lg p-5 flex gap-3">
          <input data-testid={`${testidPrefix}-new-input`} value={newName} onChange={(e) => setNewName(e.target.value)} placeholder={placeholder} className={inputCls} />
          <button data-testid={`${testidPrefix}-add-button`} type="submit" className="shrink-0 flex items-center gap-2 rounded-full bg-military text-offwhite px-5 py-2.5 text-xs uppercase tracking-widest font-semibold hover:bg-military-dark transition-colors">
            <Plus size={14} /> Criar
          </button>
        </form>

        <div className="space-y-3">
          {items.map((it) => (
            <div key={it.slug} data-testid={`${testidPrefix}-item-${it.slug}`} className={`bg-white border rounded-lg p-4 flex items-center gap-3 flex-wrap ${it.active ? "border-military/10" : "border-military/10 opacity-60"}`}>
              <div className="flex gap-1">
                <button data-testid={`${testidPrefix}-up-${it.slug}`} onClick={() => move(it, -1)} className="p-2 rounded-lg hover:bg-baby transition-colors" title="Subir"><ArrowUp size={14} /></button>
                <button data-testid={`${testidPrefix}-down-${it.slug}`} onClick={() => move(it, 1)} className="p-2 rounded-lg hover:bg-baby transition-colors" title="Descer"><ArrowDown size={14} /></button>
              </div>
              {editingSlug === it.slug ? (
                <div className="flex items-center gap-2 flex-1 min-w-[200px]">
                  <input data-testid={`${testidPrefix}-name-input-${it.slug}`} value={editName} onChange={(e) => setEditName(e.target.value)} className={inputCls} autoFocus />
                  <button data-testid={`${testidPrefix}-save-${it.slug}`} onClick={() => saveEdit(it)} className="p-2 rounded-lg bg-military/10 text-military hover:bg-military/20 transition-colors" title="Salvar"><Check size={14} /></button>
                  <button data-testid={`${testidPrefix}-cancel-${it.slug}`} onClick={() => setEditingSlug(null)} className="p-2 rounded-lg hover:bg-baby transition-colors" title="Cancelar"><X size={14} /></button>
                </div>
              ) : (
                <div className="flex-1 min-w-[200px]">
                  <p className="font-semibold text-military-dark text-sm flex items-center gap-2 flex-wrap">
                    <Tags size={13} className="text-military-dark/40" />
                    <span data-testid={`${testidPrefix}-name-${it.slug}`} translate={kind === "brands" ? "no" : undefined} className={kind === "brands" ? "notranslate" : undefined}>{it.name}</span>
                    {!it.active && <span className="text-[9px] uppercase tracking-wider font-semibold bg-military-dark/10 text-military-dark/60 px-2 py-0.5 rounded-full">inativa</span>}
                  </p>
                  <p className="text-xs text-military-dark/50 mt-0.5" data-testid={`${testidPrefix}-count-${it.slug}`}>{it.product_count} produto(s) · ordem {it.order}</p>
                </div>
              )}
              <label className="flex items-center gap-2 text-xs text-military-dark/60 cursor-pointer">
                <input data-testid={`${testidPrefix}-active-${it.slug}`} type="checkbox" checked={!!it.active} onChange={() => toggleActive(it)} className="w-4 h-4 accent-[#4E5D46]" />
                {it.active ? "Ativa" : "Inativa"}
              </label>
              <div className="flex gap-1">
                <button data-testid={`${testidPrefix}-edit-${it.slug}`} onClick={() => { setEditingSlug(it.slug); setEditName(it.name); }} className="p-2 rounded-lg hover:bg-baby transition-colors" title="Renomear"><Pencil size={14} /></button>
                <button data-testid={`${testidPrefix}-delete-${it.slug}`} onClick={() => remove(it)} className="p-2 rounded-lg hover:bg-red-50 text-red-700 transition-colors" title="Excluir"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
          {items.length === 0 && <p className="text-sm text-military-dark/50">Nenhum registro ainda.</p>}
        </div>

        <div className="bg-baby/60 border border-military/10 rounded-lg p-4 text-xs text-military-dark/70 leading-relaxed">
          {helpText}
        </div>
      </div>
    </div>
  );
};
